import React, { useState, useEffect, useRef } from "react";
import { Menu } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useLocation, Link } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { conditionsData } from "../data/conditionsData";
import { trackAndNavigate } from "../utils/navigate";

const PROMO_MESSAGES = [
  "Free consultations forever",
  "£4.99 flat fee per prescription",
  "Free UK next day deliveries",
  "Late cut-off till 5pm",
];

const PromoBar = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % PROMO_MESSAGES.length);
        setVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div className="bg-[#012d5c] text-white text-xs text-center font-medium relative z-[60] overflow-hidden h-8 flex items-center justify-center">
      <span
        style={{
          transition: "opacity 0.4s ease, transform 0.4s ease",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(-6px)",
          display: "inline-block",
        }}
      >
        {PROMO_MESSAGES[index]}
      </span>
    </div>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = location.pathname === "/";
  const isTransparent = isHomePage && !isScrolled && !isHovered;

  const customTriggerStyle = `group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50 bg-transparent hover:bg-transparent ${
    isTransparent
      ? "text-white hover:text-white/80"
      : "text-[#005eb8] hover:text-[#4caf50]"
  }`;

  const conditionsByCategory = Object.entries(conditionsData).reduce((acc, [slug, data]) => {
    if (!acc[data.category]) acc[data.category] = [];
    acc[data.category].push({ title: data.title, href: `/conditions/${slug}` });
    return acc;
  }, {});

  const simpleLinks = [
    { name: "About Us", href: "/about" },
    { name: "How It Works", href: "/how-it-works" },
    { name: "Transfer Clinic", href: "/transfer-clinic" },
    { name: "Blog", href: "/blog" },
    { name: "FAQs", href: "/faq" },
  ];

  return (
    <>
      {/* Rotating info bar */}
      <PromoBar />

      {/* Sticky Nav */}
      <nav
        className={`sticky top-0 w-full z-50 transition-all duration-300 border-b ${
          isTransparent
            ? "bg-transparent border-transparent"
            : "bg-white shadow-md border-gray-100"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Main header row */}
        <div className="container mx-auto px-4 flex items-center justify-between py-3 md:py-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={`${process.env.PUBLIC_URL}/integro-logo.png`}
              alt="Integro Clinics"
              className={`h-9 md:h-12 w-auto transition-all duration-300 ${
                isTransparent ? "brightness-0 invert" : ""
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <NavigationMenu>
              <NavigationMenuList>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/about" className={customTriggerStyle}>About Us</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/how-it-works" className={customTriggerStyle}>How It Works</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Conditions Mega Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={customTriggerStyle}>
                    Conditions
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid w-[700px] grid-cols-4 gap-5 p-6 bg-white">
                      {Object.entries(conditionsByCategory).map(([category, items]) => (
                        <div key={category} className="space-y-3">
                          <h4 className="font-bold text-[#012d5c] border-b border-gray-100 pb-2 mb-2 text-xs uppercase tracking-wider">
                            {category}
                          </h4>
                          <ul className="space-y-2">
                            {items.map((item) => (
                              <li key={item.href}>
                                <Link
                                  to={item.href}
                                  className="block text-sm text-gray-600 hover:text-[#4caf50] hover:bg-slate-50 p-1.5 rounded-md transition-colors"
                                >
                                  {item.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/transfer-clinic" className={customTriggerStyle}>Transfer Clinic</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/blog" className={customTriggerStyle}>Blog</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/faq" className={customTriggerStyle}>FAQs</Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* CTAs — Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button
              variant="outline"
              className={`rounded-lg px-5 transition-all text-sm ${
                isTransparent
                  ? "border-white text-white hover:bg-white hover:text-[#005eb8] bg-transparent"
                  : "border-[#005eb8] text-[#005eb8] hover:bg-[#005eb8] hover:text-white"
              }`}
              onClick={() => trackAndNavigate("https://app.integroclinics.com/login", 'login_click', { entry_point: 'login' })}
            >
              Patient Login
            </Button>
            <Button
              className="rounded-lg bg-[#4caf50] hover:bg-[#3d8b40] text-white px-5 text-sm border-none"
              onClick={() => trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' })}
            >
              Check your eligibility
            </Button>
          </div>

          {/* Mobile: right-side actions */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Patient Login — compact outline button */}
            <a
              href="https://app.integroclinics.com/login"
              onClick={(e) => { e.preventDefault(); trackAndNavigate("https://app.integroclinics.com/login", 'login_click', { entry_point: 'login' }); }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors shrink-0 ${
                isTransparent
                  ? "border-white text-white hover:bg-white/10"
                  : "border-[#005eb8] text-[#005eb8] hover:bg-[#e0f2fe]"
              }`}
            >
              Login
            </a>
            {/* Check your eligibility — green button */}
            <a
              href="https://app.integroclinics.com/v2/registration"
              onClick={(e) => { e.preventDefault(); trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' }); }}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#4caf50] hover:bg-[#3d8b40] text-white transition-colors shrink-0"
            >
              Check eligibility
            </a>
            {/* Hamburger for full nav */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`shrink-0 ${isTransparent ? "text-white hover:bg-white/10" : "text-[#005eb8]"}`}
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white overflow-y-auto w-80 max-w-full">
                <div className="flex flex-col space-y-4 mt-8 px-2">
                  <img
                    src={`${process.env.PUBLIC_URL}/integro-logo.png`}
                    alt="Integro Clinics"
                    className="h-10 w-auto mb-2"
                  />

                  {simpleLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      className="text-base font-medium text-[#005eb8] py-2 border-b border-gray-100"
                    >
                      {link.name}
                    </Link>
                  ))}

                  {/* Mobile Conditions Accordion */}
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="conditions" className="border-b border-gray-100">
                      <AccordionTrigger className="text-base font-medium text-[#005eb8] py-2 hover:no-underline">
                        Conditions
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-4 space-y-4 pt-2">
                          {Object.entries(conditionsByCategory).map(([category, items]) => (
                            <div key={category}>
                              <h5 className="font-bold text-[#012d5c] text-xs mb-2 uppercase tracking-wider">{category}</h5>
                              <ul className="space-y-1.5 border-l-2 border-gray-100 pl-3">
                                {items.map((item) => (
                                  <li key={item.href}>
                                    <Link
                                      to={item.href}
                                      className="text-gray-600 text-sm block py-0.5 hover:text-[#4caf50]"
                                    >
                                      {item.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <div className="flex flex-col gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="rounded-lg border-[#005eb8] text-[#005eb8] w-full"
                      onClick={() => trackAndNavigate("https://app.integroclinics.com/login", 'login_click', { entry_point: 'login' })}
                    >
                      Patient Login
                    </Button>
                    <Button
                      className="rounded-lg bg-[#4caf50] hover:bg-[#3d8b40] text-white w-full"
                      onClick={() => trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' })}
                    >
                      Check your eligibility
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
